/**
 * GenericMediaSection — a standalone image with optional caption.
 *
 * Props:
 *   imageUrl  string — URL of the image to display
 *   caption   string — optional caption below the image
 *   alignment 'center'|'left'|'right'   default 'center'
 *   width     'full'|'wide'|'narrow'    default 'full'
 *
 * Renders nothing when imageUrl is empty.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography } from '@mui/material';
import RevealOnScroll from '../../components/RevealOnScroll';

const MAX_WIDTH_MAP = {
  full: 'lg',
  wide: 'md',
  narrow: 'sm',
};

const ALIGN_MAP = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export default function GenericMediaSection({ imageUrl, caption, alignment, width }) {
  if (!imageUrl) return null;

  const maxWidth = MAX_WIDTH_MAP[width] || 'lg';
  const alignItems = ALIGN_MAP[alignment] || 'center';

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 6 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth={maxWidth}>
        <RevealOnScroll>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems,
              gap: 1.5,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={caption || ''}
              loading="lazy"
              decoding="async"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 3,
                display: 'block',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 8px 32px rgba(0,0,0,0.4)'
                    : '0 8px 32px rgba(0,0,0,0.1)',
              }}
            />
            {caption && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  fontStyle: 'italic',
                  textAlign: alignment || 'center',
                  display: 'block',
                }}
              >
                {caption}
              </Typography>
            )}
          </Box>
        </RevealOnScroll>
      </Container>
    </Box>
  );
}

GenericMediaSection.propTypes = {
  imageUrl: PropTypes.string,
  caption: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  width: PropTypes.oneOf(['full', 'wide', 'narrow']),
};

GenericMediaSection.defaultProps = {
  alignment: 'center',
  width: 'full',
};
